import React, { useEffect, useState } from 'react';
import { BrandColor } from '@styleseat/brand';
import { useStore } from 'react-redux';
import LogRocket from 'logrocket';
import analytics from '../../../../modules/analytics';
import type { PublicProvider } from '../../../../api/Providers';
import {
  HeaderText,
  PortalOverlay,
  PortalOverlayModal,
  Stack,
} from '../../../ui';
import { useTapToPay } from './useTapToPay';
import ClientTipSelectionStep from './steps/ClientTipSelectionStep';
import UserInstructionStep from './steps/UserInstructionStep';
import { CollectClientContactInfoStep } from './steps/CollectClientContactInfoStep';
import { ProAppointment } from '../../../../api/Providers/Appointments';
import { ReaderStatusIndicator } from './ReaderStatusIndicator';
import { TapToPayPhoneIcon } from './icons/TapToPayPhone';
import { IProviderClient } from '../../../../api/Providers/Clients/types';
import { useAsyncEffect } from '../../../../../../packages/hooks';
import { getClient, updateClient } from '../../../../api/Providers/Clients';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { FlowStep } from './constants';
import {
  CardErrorState,
  TransactionErrorState,
  PaymentErrorState,
  OngoingCallErrorState,
} from './ErrorState/ErrorState';
import { waitForReaderConnection, inIOSApp } from '../../../../modules/TapToPay';
import { ReaderStatus } from './TapToPayContext';
import {
  PaymentIntent,
  StripeTerminalCanceledError,
  StripeTerminalError,
} from '../../../../store/StripeTerminal';
import { isProduction } from '../../../../modules/env';
import FeatureFlags from '../../../../modules/FeatureFlags';
import { log } from '../../../../modules/TapToPay/TapToPayLogger';

export enum TipSelectionEvent {
  Submitted = 'Checkout Tap2Pay Tips Submitted',
}

export enum ContactInfoEvent {
  Viewed = 'Checkout Tap2Pay Contact Info Viewed',
  Saved = 'Checkout Tap2Pay Contact Info Saved',
  SaveFailed = 'Checkout Tap2Pay Contact Info Save Failed',
  Waived = 'Checkout Tap2Pay Contact Info Receipt Waived',
}

export enum PaymentEvent {
  Started = 'Checkout Tap2Pay Started',
  Completed = 'Checkout Tap2Pay Completed',
  Failed = 'Checkout Tap2Pay Failed',
  Canceled = 'Checkout Tap2Pay Canceled',
}

export enum ErrorEvent {
  Dismissed = 'Tap to Pay Checkout Error Dismissed',
}

export type TapToPayFlowProps = {
  appointment: ProAppointment;
  provider: Pick<PublicProvider, 'id' | 'name' | 'profile_photo'>;
  amount: number;
  defaultStep?: FlowStep;
  connectionProgress?: number;
  onCheckoutSuccess?: () => Promise<void>;
  onCheckoutFailure?: () => Promise<void>;
  onUseDifferentPaymentMethodClick: () => void;
};

const TapToPayFlow: React.FC<TapToPayFlowProps> = ({
  appointment,
  provider,
  amount,
  defaultStep = FlowStep.ProHandPhoneToClient,
  connectionProgress = 0,
  onCheckoutSuccess,
  onCheckoutFailure,
  onUseDifferentPaymentMethodClick,
}) => {
  const store = useStore();
  const [currentStep, setCurrentStep] = useState<FlowStep>(defaultStep);
  const {
    showTapToPayFlow,
    transactionErrorFlag,
    closeTapToPayFlow,
    createPayment,
    checkout,
    readerStatus,
    setReaderStatus,
  } = useTapToPay();
  const [loading, setLoading] = useState<boolean>(false);
  const [tipAmount, setTipAmount] = useState<number | null>(null);
  const [client, setClient] = useState<IProviderClient | null>(null);
  const [updatingClient, setUpdatingClient] = useState<boolean>(false);

  // TODO: Currently unused error screen that we aren't removing since
  // it may be used in a future story (EV-3892)
  useEffect(() => {
    if (transactionErrorFlag?.enabled) {
      log('Simulated error due to transaction error flag');
      setCurrentStep(FlowStep.TransactionError);
    } else {
      setCurrentStep(defaultStep);
    }
  }, [
    defaultStep,
    transactionErrorFlag?.enabled,
  ]);

  useAsyncEffect(async () => {
    try {
      const result = await getClient(provider.id, appointment.client_id);
      setClient(result);
    } catch (e) {
      nonCriticalException(e);
      setClient(null);
    }
  }, [appointment.client_id]);

  const trackError = React.useCallback((error: unknown) => {
    const errorName = error instanceof Error ? error?.name : (String(error) || 'Unknown error');
    const errorCode = error instanceof StripeTerminalError && !!(error?.code)
      ? { errorCode: error.code }
      : {};
    const baseEventPayload = {
      error: errorName,
      appointment_id: appointment.id,
    };
    const baseLogRocketPayload = {
      error: errorName,
      ...errorCode,
    };

    if (error instanceof StripeTerminalCanceledError) {
      analytics.track(
        PaymentEvent.Canceled,
        { ...baseEventPayload, reader_message_displayed: error.message },
      );
      LogRocket.track('Tap to Pay Checkout Canceled', {
        ...baseLogRocketPayload,
        reader_message_displayed: error.message,
      });
    } else {
      analytics.track(PaymentEvent.Failed, baseEventPayload);
      LogRocket.track('Tap to Pay Checkout Error', baseLogRocketPayload);
    }
  }, [appointment]);

  const handleCloseRequest = React.useCallback(() => {
    analytics.track('pro_tap_to_pay_flow_modal_closed', {
      step: currentStep,
    });
    closeTapToPayFlow();
    setCurrentStep(defaultStep);
  }, [
    closeTapToPayFlow,
    defaultStep,
    currentStep,
  ]);

  const handleCheckoutSuccess = React.useCallback(async () => {
    analytics.track(PaymentEvent.Completed);
    handleCloseRequest();
    setLoading(false);
    await onCheckoutSuccess?.();
  }, [
    handleCloseRequest,
    onCheckoutSuccess,
  ]);

  /** setStepFromError
   * @param {Error} error An instance of the Error class
   *
   * Sets the current step based on the error. If the error is unrecognized, falls back to
   * the PaymentError step.
   *
   * _Note: At the moment we are unable to discern whether a collect payment error was due to
   * pro drop out, merchant blocked, invalid merchant, failed to prep reader, invalid On
   * Behalf Of parameter, so checkout throws StripeTerminalCollectPaymentError._
  */
  const setStepFromError = React.useCallback((error: unknown) => {
    switch (error instanceof Error && error?.name) {
      case 'StripeTerminalCommandNotAllowedDuringCallError':
        setCurrentStep(FlowStep.OngoingCallError);
        break;
      case 'StripeTerminalProcessPaymentError':
      case 'StripeTerminalCaptureError':
        setCurrentStep(FlowStep.CardError);
        break;
      default:
        setCurrentStep(FlowStep.PaymentError);
    }
  }, [setCurrentStep]);

  const handleProHandsPhoneToClient = React.useCallback(() => {
    setCurrentStep(FlowStep.ClientTipSelection);
  }, [setCurrentStep]);

  const handleCheckoutFailure = React.useCallback(async (e: unknown, logMessage: string) => {
    setLoading(false);
    setStepFromError(e);
    trackError(e);
    log(logMessage, e);
    nonCriticalException(e);
    LogRocket.captureException(e instanceof Error ? e : new Error(String(e)), {
      tags: {
        TapToPay: 'Checkout',
      },
    });
    await onCheckoutFailure?.();
  }, [
    onCheckoutFailure,
    setLoading,
    setStepFromError,
    trackError,
  ]);

  type AttemptCheckoutOptions = {
    tip: number | null;
    retry?: boolean;
  };

  const attemptCheckout = React.useCallback(
    async ({ tip, retry = false }: AttemptCheckoutOptions) => {
      if (loading) {
        return; // Prevent duplicate requests
      }

      setLoading(true);
      analytics.track(PaymentEvent.Started, { retry });

      if (inIOSApp()) {
        try {
          setReaderStatus(ReaderStatus.Connecting);
          await waitForReaderConnection({ getState: store.getState });
        } catch (e) {
          await handleCheckoutFailure(e, 'Unable to connect');
          return;
        }
      }

      setReaderStatus(ReaderStatus.Initializing);
      let paymentIntent: PaymentIntent;
      try {
        ({ payment_intent: paymentIntent } = await createPayment(
          appointment,
          amount,
          tip ?? 0,
        ));
      } catch (e) {
        await handleCheckoutFailure(e, 'Unable to create payment');
        return;
      }

      // Perform checkout on the BE
      try {
        const capturedPaymentIntent = await checkout(appointment.id, paymentIntent);
        log('Payment Captured', capturedPaymentIntent.id);
        await handleCheckoutSuccess();
      } catch (e) {
        await handleCheckoutFailure(e, 'Unable to checkout');
      }
    }, [
      checkout,
      createPayment,
      handleCheckoutSuccess,
      handleCheckoutFailure,
      setLoading,
      setReaderStatus,
      loading,
      amount,
      store.getState,
      appointment,
    ],
  );

  const navToClientHandPhoneToPro = React.useCallback(async (tip: number | null) => {
    setReaderStatus(ReaderStatus.Ready);
    setCurrentStep(FlowStep.ClientHandPhoneToPro);
    await attemptCheckout({ tip: tip ?? 0 });
  }, [
    setReaderStatus,
    setCurrentStep,
    attemptCheckout,
  ]);

  const handleClientTipSelected = React.useCallback(tip => {
    analytics.track(TipSelectionEvent.Submitted, { tip_amount: tip.toFixed(2) });
    setTipAmount(tip);
    if (client?.email) {
      // Skip collecting contact info for client
      navToClientHandPhoneToPro(tip);
    } else {
      setCurrentStep(FlowStep.CollectClientContactInfo);
      analytics.track(ContactInfoEvent.Viewed);
    }
  }, [
    client,
    navToClientHandPhoneToPro,
    setTipAmount,
  ]);

  const handleContactInfoSubmit = React.useCallback(async (email: string | null) => {
    if (client?.id && email !== null) {
      setUpdatingClient(true);
      let success = false;
      try {
        await updateClient(provider.id, {
          id: client.id,
          email,
        });
        success = true;
      } catch (e) {
        analytics.track(ContactInfoEvent.SaveFailed);
        nonCriticalException(e);
      } finally {
        setUpdatingClient(false);
      }
      if (success) analytics.track(ContactInfoEvent.Saved);
    } else {
      analytics.track(ContactInfoEvent.Waived);
    }

    navToClientHandPhoneToPro(tipAmount);
  }, [
    client?.id,
    provider.id,
    tipAmount,
    navToClientHandPhoneToPro,
  ]);

  const handleTryAgainPress = React.useCallback(async () => {
    if (loading) return;
    analytics.track('pro_ttp_error_try_again_clicked', { step: currentStep });

    try {
      if (!isProduction()) {
        // Testing flags/switches may have changed, so we should force a refresh
        FeatureFlags.cache.clear();
        await FeatureFlags.getFlags();
      }
    } catch (e) {
      nonCriticalException(e);
    }

    setCurrentStep(FlowStep.ClientHandPhoneToPro);
    await attemptCheckout({ tip: tipAmount, retry: true });
  }, [
    loading,
    currentStep,
    tipAmount,
    attemptCheckout,
    setCurrentStep,
  ]);

  const handleClose = React.useCallback(() => {
    analytics.track(ErrorEvent.Dismissed, { error: currentStep });
    handleCloseRequest();
  }, [
    currentStep,
    handleCloseRequest,
  ]);

  const handleUseOtherPaymentPress = React.useCallback(() => {
    analytics.track('pro_ttp_error_use_other_payment_clicked', { step: currentStep });
    onUseDifferentPaymentMethodClick();
    handleCloseRequest();
  }, [
    onUseDifferentPaymentMethodClick,
    handleCloseRequest,
    currentStep,
  ]);

  return showTapToPayFlow ? (
    <PortalOverlay name="tap-to-pay-flow">
      <PortalOverlayModal position="fullScreen" testID="tap-to-pay-flow-modal">
        {currentStep === FlowStep.ProHandPhoneToClient && (
          <UserInstructionStep
            message="Show phone to the client to select a tip"
            onClickContinue={handleProHandsPhoneToClient}
            loading={false}
          />
        )}

        {currentStep === FlowStep.ClientTipSelection && (
          <ClientTipSelectionStep
            appointment={appointment}
            provider={provider}
            appointmentCost={amount}
            onConfirmTip={handleClientTipSelected}
          />
        )}

        {currentStep === FlowStep.CollectClientContactInfo && (
          <CollectClientContactInfoStep
            loading={updatingClient}
            onSubmit={handleContactInfoSubmit}
          />
        )}

        {currentStep === FlowStep.ClientHandPhoneToPro && (
          <UserInstructionStep
            loading={loading}
            footer={() => null}
          >
            <TapToPayPhoneIcon width={137} height={82} />
            <Stack space={16} color={BrandColor.BackInBlack} testID="tap-to-pay-checkout-status">
              <HeaderText level={3} color={BrandColor.WhiteWash}>
                Tap to Pay on iPhone
              </HeaderText>
              <ReaderStatusIndicator
                status={readerStatus}
                testID="client-hand-phone-to-pro"
                progress={connectionProgress}
              />
            </Stack>
          </UserInstructionStep>
        )}

        {currentStep === FlowStep.TransactionError && (
          <TransactionErrorState
            loading={loading}
            onClose={handleClose}
            onTryAgainPress={handleTryAgainPress}
            onUseOtherPaymentPress={handleUseOtherPaymentPress}
          />
        )}

        {currentStep === FlowStep.PaymentError && (
          <PaymentErrorState
            loading={loading}
            onClose={handleClose}
            onTryAgainPress={handleTryAgainPress}
            onUseOtherPaymentPress={handleUseOtherPaymentPress}
          />
        )}

        {currentStep === FlowStep.CardError && (
          <CardErrorState
            loading={loading}
            onClose={handleClose}
            onTryAgainPress={handleTryAgainPress}
            onUseOtherPaymentPress={handleUseOtherPaymentPress}
          />
        )}

        {currentStep === FlowStep.OngoingCallError && (
          <OngoingCallErrorState
            loading={loading}
            onClose={handleClose}
            onTryAgainPress={handleTryAgainPress}
            onUseOtherPaymentPress={handleUseOtherPaymentPress}
          />
        )}
      </PortalOverlayModal>
    </PortalOverlay>
  ) : null;
};

export default TapToPayFlow;
